/**
 * Analytics derived 100% locally from pages + blocks (PowerSync/SQLite).
 *
 * No extra table or round-trip: we count words in the plain block text, derive
 * the link map from the wiki-links serialized in `block.content`
 * (`"pageId":"<uuid>"`), and group writing activity by day from `updated_at`.
 * Everything is memoized and reactive to the local database.
 */
import { createMemo } from "solid-js";
import type { Page } from "@apunte-digital/powersync";
import { useAllBlocksQuery } from "@apunte-digital/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { usePageTree } from "@/features/notes/hooks/use-page-tree";

interface DayActivity {
  /** ISO date (YYYY-MM-DD). */
  date: string;
  /** Blocks edited that day. */
  count: number;
}

interface LinkedPageStat {
  page: Page;
  count: number;
}

const ACTIVITY_DAYS = 14;
const TOP_LINKED_LIMIT = 6;
const WIKILINK_RE = /"pageId":"([^"]+)"/g;

function countWords(text: string | null): number {
  if (!text) {
    return 0;
  }
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return 0;
  }
  return trimmed.split(/\s+/u).length;
}

/** Local day key (YYYY-MM-DD) from an ISO timestamp. */
function dayKey(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function useNotesStats() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const { pages } = usePageTree();

  const blocks = useAllBlocksQuery({
    db: () => db(),
    drizzle,
    userId,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  const allPages = createMemo<Page[]>(() => pages.data ?? []);
  const allBlocks = createMemo(() => blocks.data ?? []);

  const isLoading = () => pages.isLoading || blocks.isLoading;

  const totalPages = createMemo(() => allPages().length);

  const totalWords = createMemo(() => allBlocks().reduce((sum, b) => sum + countWords(b.text), 0));

  const totalBlocks = createMemo(() => allBlocks().length);

  const totalLinks = createMemo(() => {
    let count = 0;
    for (const b of allBlocks()) {
      if (!b.content) {
        continue;
      }
      const matches = b.content.match(WIKILINK_RE);
      count += matches?.length ?? 0;
    }
    return count;
  });

  /** Writing activity by day (last ACTIVITY_DAYS, chronological order). */
  const activity = createMemo<DayActivity[]>(() => {
    const counts = new Map<string, number>();
    for (const b of allBlocks()) {
      const key = dayKey(b.updated_at);
      if (key) {
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Array.from({ length: ACTIVITY_DAYS }, (_unused, index) => {
      const offset = ACTIVITY_DAYS - 1 - index;
      const d = new Date(today);
      d.setDate(today.getDate() - offset);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const key = `${year}-${month}-${day}`;
      return { date: key, count: counts.get(key) ?? 0 };
    });
  });

  /** Pages with the most incoming wiki-links (link map). */
  const topLinkedPages = createMemo<LinkedPageStat[]>(() => {
    const incoming = new Map<string, number>();
    for (const b of allBlocks()) {
      if (!b.content) {
        continue;
      }
      for (const match of b.content.matchAll(WIKILINK_RE)) {
        const targetId = match[1];
        if (targetId) {
          incoming.set(targetId, (incoming.get(targetId) ?? 0) + 1);
        }
      }
    }
    const byId = new Map(allPages().map((p) => [p.id, p]));
    return [...incoming.entries()]
      .map(([id, count]) => {
        const page = byId.get(id);
        return page ? { page, count } : null;
      })
      .filter((entry): entry is LinkedPageStat => entry !== null)
      .toSorted((a, b) => b.count - a.count)
      .slice(0, TOP_LINKED_LIMIT);
  });

  /** Longest pages by number of blocks. */
  const longestPages = createMemo<LinkedPageStat[]>(() => {
    const blocksPerPage = new Map<string, number>();
    for (const b of allBlocks()) {
      if (b.page_id) {
        blocksPerPage.set(b.page_id, (blocksPerPage.get(b.page_id) ?? 0) + 1);
      }
    }
    const byId = new Map(allPages().map((p) => [p.id, p]));
    return [...blocksPerPage.entries()]
      .map(([id, count]) => {
        const page = byId.get(id);
        return page ? { page, count } : null;
      })
      .filter((entry): entry is LinkedPageStat => entry !== null)
      .toSorted((a, b) => b.count - a.count)
      .slice(0, TOP_LINKED_LIMIT);
  });

  const hasData = createMemo(() => totalPages() > 0 || totalBlocks() > 0);

  return {
    isLoading,
    hasData,
    totalPages,
    totalWords,
    totalBlocks,
    totalLinks,
    activity,
    topLinkedPages,
    longestPages,
  };
}
